import type { GameState, CommandResult, LogEntry } from '../types/game';
import type { StateManager } from '../engine/state';
import {
  startFish, cancelFish, sell, showInventory, buy, showStatus, showShop,
  showHelp, exportSave, importSave, travel, look, showMap,
  showTutorial, allocateSkill, upgradeEquipment, upgradeProperty,
  showEquipment, showProperties, debugCommand,
  renameVessel,
} from '../game/actions';
import { showAchievements } from '../game/achievements';
import { getVesselDefById, getVesselCap, VESSEL_DEFS } from '../game/vessels';
import { getPropertyTierName, PROPERTY_DEFS } from '../game/properties';
import { getLocationDefById, LOCATION_DEFS, isLocationUnlocked } from '../game/locations';
import { FISH_SPECIES } from '../game/fish';
import { SKILL_DEFS } from '../game/skills';
import { EQUIPMENT_DEFS } from '../game/equipment';
import { BYCATCH_ITEMS } from '../game/bycatch';
import { makeEntry, fmtCoins, spacer } from '../ui/format';

export type CommandHandler = (state: GameState, args: string[]) => CommandResult;

interface CommandMap {
  [key: string]: CommandHandler;
}

// ── Fleet submenu types ──

type FleetPhase = 'list' | 'rename_prompt' | 'sell_confirm';

interface FleetSubmenuVessel {
  name: string;
  vesselId: string;
  vesselDefName: string;
  sellPrice: number;
  onTrip: boolean;
}

interface FleetSubmenu {
  type: 'fleet';
  vessels: FleetSubmenuVessel[];
  phase: FleetPhase;
  mode: 'rename' | 'sell';
  selectedIndex: number;
}

export class CommandParser {
  private commands: CommandMap;
  private activeSubmenu: FleetSubmenu | null = null;

  constructor(private stateManager: StateManager) {
    this.commands = {
      help: () => showHelp(this.stateManager.getState()),
      tutorial: () => showTutorial(this.stateManager.getState()),
      fish: () => this.runWithState(startFish),
      cancel: () => this.runWithState(cancelFish),
      sell: (_state, args) => this.runWithState(sell, args),
      inventory: () => this.runWithState(showInventory),
      inv: () => this.runWithState(showInventory),
      buy: (_state, args) => this.runWithState(buy, args),
      shop: () => this.runWithState(showShop),
      status: () => this.runWithState(showStatus),
      achievements: () => this.runWithState(showAchievements),
      export: () => this.runWithState(exportSave),
      import: (_state, args) => this.runWithState(importSave, args),
      look: () => this.runWithState(look),
      l: () => this.runWithState(look),
      go: (_state, args) => this.runWithState(travel, args),
      travel: (_state, args) => this.runWithState(travel, args),
      move: (_state, args) => this.runWithState(travel, args),
      map: () => this.runWithState(showMap),
      debug: (_state, args) => this.runWithState(debugCommand, args),
      allocate: (_state, args) => this.runWithState(allocateSkill, args),
      upgrade: (_state, args) => {
        if (args[0]?.toLowerCase() === 'home' || args[0]?.toLowerCase() === 'dock') {
          return this.runWithState(upgradeProperty, args);
        }
        return this.runWithState(upgradeEquipment, args);
      },
      equipment: () => this.runWithState(showEquipment),
      equip: () => this.runWithState(showEquipment),
      gear: () => this.runWithState(showEquipment),
      property: () => this.runWithState(showProperties),
      properties: () => this.runWithState(showProperties),
      fleet: () => this.openFleetSubmenu(),
      rename: (_state, args) => this.runWithState(renameVessel, args),
      clear: () => ({ logEntries: [], statePatch: {} }),
      reset: (_state, args) => {
        if (args[0]?.toLowerCase() !== 'confirm') {
          return {
            logEntries: [{
              text: 'This will delete ALL progress. Type >reset confirm to proceed.',
              type: 'error',
              timestamp: Date.now(),
            }],
            statePatch: {},
          };
        }
        const entry = this.stateManager.reset();
        return { logEntries: [entry], statePatch: {}, clear: true };
      },
      f: () => this.runWithState(startFish),
      i: () => this.runWithState(showInventory),
      s: () => this.runWithState(showStatus),
      m: () => this.runWithState(showMap),
    };
  }

  // ── Fleet submenu ──

  private openFleetSubmenu(): CommandResult {
    const state = this.stateManager.getState();
    const loc = state.currentLocation;

    if (loc !== 'dock' && loc !== 'harbor') {
      return { logEntries: [makeEntry('Go to your dock to manage your fleet, or the harbor to sell vessels.', 'error')], statePatch: {} };
    }

    if (state.vessels.length === 0) {
      return { logEntries: [makeEntry('You don\'t own any vessels. Visit the Shipyard Harbor to buy one.', 'info')], statePatch: {} };
    }

    const mode: 'rename' | 'sell' = loc === 'harbor' ? 'sell' : 'rename';

    const vessels: FleetSubmenuVessel[] = state.vessels.map(v => {
      const def = getVesselDefById(v.vesselId);
      return {
        name: v.name,
        vesselId: v.vesselId,
        vesselDefName: def?.name || v.vesselId,
        sellPrice: Math.round((def?.cost ?? 0) * 0.5 * 100) / 100,
        onTrip: state.vesselTrips.some(t => t.name === v.name),
      };
    });

    this.activeSubmenu = {
      type: 'fleet',
      vessels,
      phase: 'list',
      mode,
      selectedIndex: -1,
    };

    const result = this.renderFleetList(this.activeSubmenu);
    return { ...result, statePatch: {} };
  }

  private renderFleetList(sub: FleetSubmenu): CommandResult {
    const state = this.stateManager.getState();
    const dockProp = state.properties.find(p => p.id === 'dock');
    const dockTier = dockProp?.tier ?? 0;
    const dockName = dockProp ? getPropertyTierName('dock', dockTier) : 'no dock';
    const cap = getVesselCap(state.playerStats.skills.business.level, dockTier);

    const logEntries: LogEntry[] = [
      spacer(),
      makeEntry('═══ Fleet Management ═══', 'system'),
      makeEntry(`${dockName} — ${sub.vessels.length}/${cap} berths`, 'info'),
      spacer(),
    ];

    for (let i = 0; i < sub.vessels.length; i++) {
      const v = sub.vessels[i];
      const status = v.onTrip ? '⏳ On trip' : '⚓ Docked';
      logEntries.push(makeEntry(`  ${i + 1}. ${v.name} (${v.vesselDefName}) — ${status}`, 'item'));
    }

    logEntries.push(spacer());

    if (sub.mode === 'rename') {
      logEntries.push(makeEntry('Type a number to rename a vessel, or >back to exit.', 'command'));
    } else {
      logEntries.push(makeEntry('Type a number to sell a vessel (50% price), or >back to exit.', 'command'));
    }

    logEntries.push(spacer());
    return { logEntries, statePatch: {} };
  }

  private redrawFleetList(): CommandResult {
    const state = this.stateManager.getState();

    if (state.vessels.length === 0) {
      this.activeSubmenu = null;
      return {
        logEntries: [spacer(), makeEntry('No more vessels. Exiting fleet management.', 'system')],
        statePatch: {},
      };
    }

    const vessels: FleetSubmenuVessel[] = state.vessels.map(v => {
      const def = getVesselDefById(v.vesselId);
      return {
        name: v.name,
        vesselId: v.vesselId,
        vesselDefName: def?.name || v.vesselId,
        sellPrice: Math.round((def?.cost ?? 0) * 0.5 * 100) / 100,
        onTrip: state.vesselTrips.some(t => t.name === v.name),
      };
    });

    this.activeSubmenu = {
      type: 'fleet',
      vessels,
      phase: 'list',
      mode: this.activeSubmenu!.mode,
      selectedIndex: -1,
    };

    return this.renderFleetList(this.activeSubmenu);
  }

  // ── Submenu routing ──

  private handleSubmenu(input: string): { logEntries: LogEntry[]; clear?: boolean; transitTo?: string; success: boolean } {
    const lower = input.toLowerCase();

    if (lower === 'back' || lower === 'close' || lower === 'exit') {
      this.activeSubmenu = null;
      return {
        logEntries: [makeEntry('Exited fleet management.', 'system')],
        success: true,
      };
    }

    if (this.activeSubmenu!.type === 'fleet') {
      return this.handleFleetSubmenu(this.activeSubmenu!, input);
    }

    this.activeSubmenu = null;
    return {
      logEntries: [makeEntry('Submenu error. Returning to normal.', 'error')],
      success: false,
    };
  }

  private handleFleetSubmenu(sub: FleetSubmenu, input: string): { logEntries: LogEntry[]; clear?: boolean; transitTo?: string; success: boolean } {
    const lower = input.toLowerCase();
    const state = this.stateManager.getState();

    // ── Phase: list ──
    if (sub.phase === 'list') {
      const n = parseInt(input, 10);
      if (isNaN(n) || n < 1 || n > sub.vessels.length) {
        return {
          logEntries: [makeEntry(`Type a number 1-${sub.vessels.length}, or back to exit.`, 'error')],
          success: false,
        };
      }

      sub.selectedIndex = n - 1;
      const vessel = sub.vessels[sub.selectedIndex];

      if (sub.mode === 'rename') {
        sub.phase = 'rename_prompt';
        return {
          logEntries: [makeEntry(`Enter a new name for "${vessel.name}" (${vessel.vesselDefName}):`, 'system')],
          success: true,
        };
      }

      if (sub.mode === 'sell') {
        sub.phase = 'sell_confirm';
        return {
          logEntries: [makeEntry(`Sell "${vessel.name}" (${vessel.vesselDefName}) for ${fmtCoins(vessel.sellPrice)} coins? Type yes or no.`, 'system')],
          success: true,
        };
      }

      return { logEntries: [makeEntry('Unknown mode.', 'error')], success: false };
    }

    // ── Phase: rename_prompt ──
    if (sub.phase === 'rename_prompt') {
      const newName = input.trim();
      if (newName.length > 30) {
        return {
          logEntries: [makeEntry('Name too long. Max 30 characters.', 'error')],
          success: false,
        };
      }

      if (state.vessels.some(v => v.name.toLowerCase() === newName.toLowerCase())) {
        return {
          logEntries: [makeEntry(`A vessel named "${newName}" already exists.`, 'error')],
          success: false,
        };
      }

      const oldName = sub.vessels[sub.selectedIndex].name;
      const newVessels = state.vessels.map(v =>
        v.name.toLowerCase() === oldName.toLowerCase() ? { ...v, name: newName } : v
      );

      this.stateManager.dispatch(
        { vessels: newVessels },
        [makeEntry(`Renamed "${oldName}" to "${newName}".`, 'success')],
      );

      const redrawResult = this.redrawFleetList();
      return {
        logEntries: redrawResult.logEntries,
        success: true,
      };
    }

    // ── Phase: sell_confirm ──
    if (sub.phase === 'sell_confirm') {
      if (lower === 'yes' || lower === 'y') {
        const vessel = sub.vessels[sub.selectedIndex];
        const sellPrice = vessel.sellPrice;

        const soldVessel = state.vessels[sub.selectedIndex];
        const newVessels = state.vessels.filter(v => v !== soldVessel);

        this.stateManager.dispatch(
          {
            coins: Math.round((state.coins + sellPrice) * 100) / 100,
            totalCoinsEarned: Math.round((state.totalCoinsEarned + sellPrice) * 100) / 100,
            vessels: newVessels,
          },
          [makeEntry(`Sold "${vessel.name}" (${vessel.vesselDefName}) for ${fmtCoins(sellPrice)} coins.`, 'success')],
        );

        const sellXp = Math.ceil(sellPrice / 3);
        this.stateManager.addPlayerXp(sellXp);

        const redrawResult = this.redrawFleetList();
        return {
          logEntries: redrawResult.logEntries,
          success: true,
        };
      }

      if (lower === 'no' || lower === 'n') {
        sub.phase = 'list';
        const redrawResult = this.redrawFleetList();
        return {
          logEntries: redrawResult.logEntries,
          success: true,
        };
      }

      return {
        logEntries: [makeEntry('Type yes or no.', 'error')],
        success: false,
      };
    }

    return { logEntries: [makeEntry('Unknown phase.', 'error')], success: false };
  }

  // ── Parser ──

  isInSubmenu(): boolean {
    return this.activeSubmenu !== null;
  }

  // ── Autocomplete ──

  private locationCommands: Record<string, string[]> = {
    fishing: ['fish', 'f', 'cancel'],
    market: ['sell', 'shop', 'equipment', 'equip', 'gear', 'upgrade'],
    harbor: ['buy', 'sell', 'fleet'],
    dock: ['fleet', 'rename', 'upgrade'],
    house: ['go', 'travel', 'move', 'upgrade', 'property', 'properties'],
  };

  private getAvailableCommands(state: GameState): string[] {
    const location = getLocationDefById(state.currentLocation);
    const locationType = location?.type ?? 'house';
    const allowedForLocation = this.locationCommands[locationType] || [];
    const allCommandNames = Object.keys(this.commands);

    return allCommandNames.filter(cmd => {
      if (cmd === 'cancel') return state.isFishing && allowedForLocation.includes(cmd);
      const isLocationRestricted = Object.values(this.locationCommands).some(list => list.includes(cmd));
      if (isLocationRestricted) return allowedForLocation.includes(cmd);
      return true;
    });
  }

  private getArgCompletions(state: GameState, cmd: string, argIndex: number, partial: string): string[] {
    const p = partial.toLowerCase();

    if (cmd === 'go' || cmd === 'travel' || cmd === 'move') {
      if (argIndex === 1) {
        return LOCATION_DEFS
          .filter(l => isLocationUnlocked(l, state) && l.id.startsWith(p))
          .map(l => l.id);
      }
    }

    if (cmd === 'buy') {
      if (argIndex === 1) {
        const hasDock = state.properties.some(prop => prop.id === 'dock');
        const docks = hasDock ? [] : ['dock'];
        const vessels = VESSEL_DEFS.filter(v => v.id.startsWith(p)).map(v => v.id);
        return [...docks.filter(d => d.startsWith(p)), ...vessels];
      }
    }

    if (cmd === 'sell') {
      if (argIndex === 1) {
        const matches: string[] = [];
        if ('all'.startsWith(p)) matches.push('all');
        matches.push(...FISH_SPECIES.filter(f => f.id.startsWith(p)).map(f => f.id));
        matches.push(...BYCATCH_ITEMS.filter(b => b.id.startsWith(p)).map(b => b.id));
        matches.push(...VESSEL_DEFS.filter(v => ('vessel_' + v.id).startsWith(p) || v.id.startsWith(p)).map(v => 'vessel_' + v.id));
        if ('vessel'.startsWith(p)) matches.push('vessel');
        return matches;
      }
    }

    if (cmd === 'allocate') {
      if (argIndex === 1) {
        return SKILL_DEFS.filter(s => s.id.startsWith(p)).map(s => s.id);
      }
    }

    if (cmd === 'rename') {
      if (argIndex >= 1) {
        return state.vessels.map(v => v.name).filter(n => n.toLowerCase().startsWith(p));
      }
    }

    if (cmd === 'upgrade') {
      if (argIndex === 1) {
        const equipMatches = EQUIPMENT_DEFS.filter(e => e.id.startsWith(p)).map(e => e.id);
        const propMatches = PROPERTY_DEFS.filter(pr => pr.id.startsWith(p)).map(pr => pr.id);
        return [...equipMatches, ...propMatches];
      }
    }

    return [];
  }

  private getSubmenuCompletions(partial: string): string[] {
    if (!this.activeSubmenu || this.activeSubmenu.type !== 'fleet') return [];
    const p = partial.toLowerCase();
    const escape = ['back', 'close', 'exit'].filter(c => c.startsWith(p));

    if (this.activeSubmenu.phase === 'list') {
      const numbers = Array.from({ length: this.activeSubmenu.vessels.length }, (_, i) => String(i + 1))
        .filter(n => n.startsWith(p));
      return [...numbers, ...escape];
    }

    if (this.activeSubmenu.phase === 'sell_confirm') {
      return ['yes', 'no', 'y', 'n'].filter(c => c.startsWith(p));
    }

    return escape;
  }

  getCompletions(state: GameState, parts: string[], wordPos: number): string[] {
    if (this.activeSubmenu) {
      const partial = (parts[wordPos] || '').toLowerCase();
      return this.getSubmenuCompletions(partial);
    }

    if (wordPos === 0) {
      const partial = (parts[0] || '').toLowerCase();
      const cmds = this.getAvailableCommands(state);
      return cmds.filter(c => c.startsWith(partial));
    }

    const cmd = parts[0].toLowerCase();
    const partial = (parts[wordPos] || '').toLowerCase();
    return this.getArgCompletions(state, cmd, wordPos, partial);
  }

  parse(input: string): { logEntries: LogEntry[]; clear?: boolean; transitTo?: string; success: boolean } {
    const trimmed = input.trim();
    if (!trimmed) {
      return { logEntries: [], success: false };
    }

    // ── Submenu routing ──
    if (this.activeSubmenu !== null) {
      return this.handleSubmenu(trimmed);
    }

    const parts = trimmed.split(/\s+/);
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);

    if (cmd === 'clear') {
      return { logEntries: [], clear: true, success: true };
    }

    const handler = this.commands[cmd];
    if (!handler) {
      return {
        logEntries: [{
          text: `Unknown command: "${cmd}". Type >help for available commands.`,
          type: 'error',
          timestamp: Date.now(),
        }],
        success: false,
      };
    }

    const state = this.stateManager.getState();
    const result: CommandResult = handler(state, args);

    const hadStatePatch = Object.keys(result.statePatch).length > 0;

    if (hadStatePatch) {
      this.stateManager.dispatch(result.statePatch, result.logEntries);
      return { logEntries: [], clear: result.clear, transitTo: result.transitTo, success: true };
    }

    const isError = result.logEntries.length > 0 && result.logEntries[0].type === 'error';
    return { logEntries: result.logEntries, clear: result.clear, transitTo: result.transitTo, success: !isError };
  }

  private runWithState(fn: (state: GameState, args: string[]) => CommandResult, args: string[] = []): CommandResult {
    return fn(this.stateManager.getState(), args);
  }
}
